import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Calculator, TrendingUp, Wallet, Banknote, BadgeEuro, Loader2, ArrowRight } from "lucide-react";

const FINANCING_OPTIONS = [
    { value: "cash", label: "Autofinancement", icon: Wallet, hint: "Paiement comptant — ROI maximal" },
    { value: "loan_10y", label: "Prêt vert 10 ans", icon: Banknote, hint: "Mensualités lissées, pas d'apport" },
    { value: "cee_subsidy", label: "Prime CEE", icon: BadgeEuro, hint: "Subvention déduite, reste à financer" },
];

const Euros = (n) => `${Math.round(Number(n) || 0).toLocaleString("fr-FR")} €`;

export default function FinancingSimulator({ defaultKwc = 30, defaultAnnualKwh = 60000 }) {
    const [regions, setRegions] = useState([]);
    const [region, setRegion] = useState("ile-de-france");
    const [installKwc, setInstallKwc] = useState(defaultKwc);
    const [annualKwh, setAnnualKwh] = useState(defaultAnnualKwh);
    const [priceKwh, setPriceKwh] = useState(0.22);
    const [financing, setFinancing] = useState("cash");
    const [loanRate, setLoanRate] = useState(3.5);
    const [horizon, setHorizon] = useState(20);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/cee-solar/regions");
                setRegions(data);
            } catch (e) {
                if (process.env.NODE_ENV !== "production") console.error("regions load fail", e);
            }
        })();
    }, []);

    const run = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/cee-solar/financing-simulate", {
                install_kwc: Number(installKwc),
                annual_kwh_consumption: Number(annualKwh),
                region,
                current_price_kwh: Number(priceKwh),
                financing,
                loan_rate_pct: Number(loanRate),
                horizon_years: Number(horizon),
            });
            setResult(data);
            toast.success(`Plan ${data.financing_plan.split(" ")[0].toLowerCase()} — payback ${data.payback_years} ans`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    // Mini bar-chart with the cumulative net balance over time
    const ChartRow = () => {
        if (!result?.projection) return null;
        const max = Math.max(...result.projection.map((p) => Math.abs(p.net_balance_eur)));
        return (
            <div className="space-y-1.5" data-testid="financing-chart">
                {result.projection.map((p) => {
                    const isNeg = p.net_balance_eur < 0;
                    const width = max > 0 ? (Math.abs(p.net_balance_eur) / max) * 100 : 0;
                    return (
                        <div key={p.year} className="flex items-center gap-2 text-xs">
                            <span className="w-10 font-mono text-muted-foreground">An {p.year}</span>
                            <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden relative">
                                <div
                                    className={`h-full rounded-full ${isNeg ? "bg-rose-500" : "bg-accent"}`}
                                    style={{ width: `${width}%` }}
                                />
                            </div>
                            <span className={`w-24 text-right font-mono font-bold ${isNeg ? "text-rose-600" : "text-primary"}`}>
                                {p.net_balance_eur >= 0 ? "+" : ""}{Math.round(p.net_balance_eur).toLocaleString("fr-FR")} €
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <Card className="rounded-3xl border-border bg-card" data-testid="financing-simulator">
            <CardContent className="p-7">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
                    <div>
                        <p className="em-eyebrow flex items-center gap-2"><Calculator className="h-3.5 w-3.5" />Simulateur ROI · 20 ans</p>
                        <h3 className="font-display text-2xl md:text-3xl font-bold text-primary mt-1">
                            Financement & retour sur investissement
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Projection multi-année avec dégradation (-0,6%/an), inflation énergie (+3%/an) et prime CEE B2B.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                        <Label className="em-eyebrow">Région</Label>
                        <Select value={region} onValueChange={setRegion}>
                            <SelectTrigger className="rounded-xl mt-2" data-testid="fin-region"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {regions.map((r) => (
                                    <SelectItem key={r.slug} value={r.slug}>
                                        {r.label} — {r.irradiance_kwh_per_kwc} kWh/kWc
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="em-eyebrow">Puissance installée (kWc)</Label>
                        <Input
                            type="number"
                            value={installKwc}
                            onChange={(e) => setInstallKwc(e.target.value)}
                            min={3}
                            max={500}
                            step={1}
                            className="rounded-xl mt-2"
                            data-testid="fin-kwc"
                        />
                    </div>
                    <div>
                        <Label className="em-eyebrow">Conso annuelle site (kWh)</Label>
                        <Input
                            type="number"
                            value={annualKwh}
                            onChange={(e) => setAnnualKwh(e.target.value)}
                            min={1000}
                            step={1000}
                            className="rounded-xl mt-2"
                            data-testid="fin-conso"
                        />
                    </div>
                    <div>
                        <Label className="em-eyebrow">Prix actuel du kWh (€)</Label>
                        <Input
                            type="number"
                            value={priceKwh}
                            onChange={(e) => setPriceKwh(e.target.value)}
                            min={0.10}
                            max={0.40}
                            step={0.01}
                            className="rounded-xl mt-2"
                            data-testid="fin-price"
                        />
                    </div>
                </div>

                <div className="mt-6">
                    <Label className="em-eyebrow">Mode de financement</Label>
                    <Tabs value={financing} onValueChange={setFinancing} className="mt-2">
                        <TabsList className="w-full rounded-full grid grid-cols-3">
                            {FINANCING_OPTIONS.map((opt) => (
                                <TabsTrigger key={opt.value} value={opt.value} className="rounded-full gap-2" data-testid={`fin-mode-${opt.value}`}>
                                    <opt.icon className="h-3.5 w-3.5" />
                                    <span className="hidden sm:inline">{opt.label}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {FINANCING_OPTIONS.map((opt) => (
                            <TabsContent key={opt.value} value={opt.value} className="mt-3">
                                <p className="text-xs text-muted-foreground">{opt.hint}</p>
                                {opt.value === "loan_10y" && (
                                    <div className="mt-2">
                                        <Label className="em-eyebrow">Taux du prêt (TAEG %)</Label>
                                        <Input
                                            type="number"
                                            value={loanRate}
                                            onChange={(e) => setLoanRate(e.target.value)}
                                            min={0}
                                            max={15}
                                            step={0.1}
                                            className="rounded-xl mt-2"
                                            data-testid="fin-rate"
                                        />
                                    </div>
                                )}
                            </TabsContent>
                        ))}
                    </Tabs>
                </div>

                <Button
                    onClick={run}
                    disabled={loading}
                    type="button"
                    size="lg"
                    className="w-full rounded-full bg-primary hover:bg-primary/90 mt-6"
                    data-testid="fin-submit"
                >
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                    {loading ? "Calcul ROI…" : "Lancer la projection 20 ans"}
                    <ArrowRight className="h-4 w-4 ml-2" />
                </Button>

                {result && (
                    <div className="mt-7 space-y-5" data-testid="financing-result">
                        <Card className="rounded-2xl bg-primary text-primary-foreground border-0">
                            <CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                                <Stat label="Payback" value={`${result.payback_years} ans`} accent />
                                <Stat label={`ROI ${result.horizon_years} ans`} value={`${result.roi_pct}%`} accent />
                                <Stat label={`Solde net ${result.horizon_years} ans`} value={Euros(result.horizon_net_balance_eur)} />
                                <Stat label="CO₂ évité" value={`${result.co2_avoided_tons_total} t`} />
                            </CardContent>
                        </Card>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <MiniCard label="Coût brut" value={Euros(result.install_cost_gross_eur)} />
                            <MiniCard label="Prime CEE" value={Euros(result.cee_subsidy_eur)} highlight={result.cee_subsidy_eur > 0} />
                            <MiniCard label="Coût net" value={Euros(result.install_cost_net_eur)} />
                            <MiniCard
                                label={result.monthly_loan_payment_eur > 0 ? "Mensualité prêt" : "Production / an"}
                                value={result.monthly_loan_payment_eur > 0
                                    ? `${Euros(result.monthly_loan_payment_eur)}/mois`
                                    : `${result.annual_production_kwh.toLocaleString("fr-FR")} kWh`}
                            />
                        </div>

                        <div>
                            <p className="em-eyebrow mb-2">Évolution du solde net cumulé · {result.region_label}</p>
                            <ChartRow />
                            <p className="text-xs text-muted-foreground mt-3 italic">
                                {result.financing_plan} — Irradiance régionale : <strong>{result.irradiance_kwh_per_kwc} kWh/kWc/an</strong>.
                                Auto-conso : <strong>{result.self_consumption_kwh.toLocaleString("fr-FR")} kWh/an</strong>
                                {" · "}
                                Surplus revendu : <strong>{result.surplus_kwh.toLocaleString("fr-FR")} kWh/an</strong>.
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <p className="text-xs uppercase tracking-wider text-primary-foreground/70">{label}</p>
            <p className={`font-display text-2xl md:text-3xl font-black mt-1 ${accent ? "text-accent" : ""}`}>{value}</p>
        </div>
    );
}

function MiniCard({ label, value, highlight }) {
    return (
        <div className={`rounded-xl p-3 border ${highlight ? "border-accent bg-accent/10" : "border-border bg-secondary"}`}>
            <p className="em-eyebrow text-[10px]">{label}</p>
            <p className="font-display font-bold text-primary mt-0.5">{value}</p>
        </div>
    );
}
